import PropTypes from "prop-types"
import { useState } from "react"
import { DownloadOutlined } from "@mui/icons-material"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material"
import type { IconButtonProps } from "@mui/material"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { useSkillDownload } from "connected-components/skills/hooks/use-skill-download"

export type SkillDownloadNoun = "workflow" | "skill"

interface SkillDownloadVariant {
  tooltip: string
  dialogBody: string
  errorMessage: string
  buttonSize: IconButtonProps["size"]
  buttonColor: IconButtonProps["color"]
  iconSize: "small" | "medium"
}

const DOWNLOAD_VARIANTS: Record<SkillDownloadNoun, SkillDownloadVariant> = {
  workflow: {
    tooltip: "Download workflow",
    dialogBody: "This will download the agentic workflow file to your device.",
    errorMessage: "Unable to download workflow. Please try again.",
    buttonSize: "small",
    buttonColor: "primary",
    iconSize: "small",
  },
  skill: {
    tooltip: "Download skill",
    dialogBody: "This will download the skill file to your device.",
    errorMessage: "Unable to download skill. Please try again.",
    buttonSize: "medium",
    buttonColor: "default",
    iconSize: "medium",
  },
}

interface SkillDownloadActionProps {
  skill: SkillCatalogSkill
  noun: SkillDownloadNoun
}

export function SkillDownloadAction({ skill, noun }: SkillDownloadActionProps) {
  const variant = DOWNLOAD_VARIANTS[noun]
  const { downloadSkill, isDownloading } = useSkillDownload(skill, variant.errorMessage)
  const [confirmOpen, setConfirmOpen] = useState(false)

  function handleConfirmDownload() {
    downloadSkill()
    setConfirmOpen(false)
  }

  return (
    <>
      <Tooltip title={variant.tooltip}>
        <span>
          <IconButton
            size={variant.buttonSize}
            color={variant.buttonColor}
            aria-label={`Download ${skill.name}`}
            disabled={isDownloading}
            loading={isDownloading}
            onClick={() => setConfirmOpen(true)}
          >
            <DownloadOutlined fontSize={variant.iconSize} />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Download {skill.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {variant.dialogBody}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmDownload}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

SkillDownloadAction.propTypes = {
  skill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    tier: PropTypes.oneOf(["licensed", "proprietary", "user", "shared"]).isRequired,
  }).isRequired,
  noun: PropTypes.oneOf(["workflow", "skill"]).isRequired,
}
